import { StandardEvents } from '@shopify/events';
import { formatMoney as formatThemeMoney } from '@theme/money-formatting';

/** How long the celebration plays before `free-shipping-progress:complete` is announced. */
const CELEBRATION_LEAD_MS = 650;

class FreeShippingProgress extends HTMLElement {
  static refreshPromise = null;
  #abortController = new AbortController();
  #celebrationTimer = 0;
  #animationCleanup = null;
  #motionAnimations = [];
  #lastTotal = null;
  #itemCount = 0;
  #successPending = false;
  #countFrame = 0;
  #lastAmount = null;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#lastTotal ??= Number(this.querySelector('[data-free-shipping-track]')?.getAttribute('aria-valuenow') || 0);
    this.#itemCount = Number(this.dataset.itemCount || 0);
    if (this.#lastAmount == null) {
      const threshold = Number(this.dataset.threshold || 79900);
      if (this.#itemCount > 0 && this.#lastTotal < threshold) this.#lastAmount = threshold - this.#lastTotal;
      else if (this.#itemCount === 0 && this.#piecePrice > 0 && this.#piecePrice < threshold) {
        this.#lastAmount = threshold - this.#piecePrice;
      }
    }

    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', this.#onMotionPreferenceChange, {
      signal: this.#abortController.signal,
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange, {
      signal: this.#abortController.signal,
    });
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartChange, {
      signal: this.#abortController.signal,
    });

    if (this.#isProductMode) {
      this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onVariantChange, {
        signal: this.#abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.#countFrame);
    window.clearTimeout(this.#celebrationTimer);
    this.#animationCleanup?.();
    this.#cancelMotion();
    this.#successPending = false;
    this.#abortController.abort();
  }

  get #isProductMode() {
    return this.dataset.variantId != null;
  }

  /** Price of the viewed piece that would still be added: 0 if unavailable or already in the cart. */
  get #piecePrice() {
    if (!this.#isProductMode || this.dataset.variantAvailable !== 'true') return 0;
    const inCart = (this.dataset.cartVariantIds || '').split(',').includes(String(this.dataset.variantId));
    return inCart ? 0 : Number(this.dataset.variantPrice || 0);
  }

  #onVariantChange = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail } = {}) => {
        const variant = detail?.resource;
        const previous = this.#piecePrice;
        this.dataset.variantId = variant?.id != null ? String(variant.id) : '';
        this.dataset.variantPrice = String(Number(variant?.price || 0));
        this.dataset.variantAvailable = String(Boolean(variant && variant.available !== false));
        if (this.#piecePrice === previous) return;
        this.#render(this.#lastTotal ?? 0, this.#itemCount, { variantOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[free-shipping-progress] Variant update failed:', error);
      });
  };

  #onCartChange = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        return FreeShippingProgress.#fetchCart();
      })
      .then((cart) => {
        if (!cart) return;
        if (this.#isProductMode && Array.isArray(cart.items)) {
          this.dataset.cartVariantIds = cart.items.map((item) => item.variant_id).join(',');
        }
        this.#render(Number(cart.total_price || 0), Number(cart.item_count || 0));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[free-shipping-progress] Cart refresh failed:', error);
      });
  };

  static #fetchCart() {
    if (!FreeShippingProgress.refreshPromise) {
      FreeShippingProgress.refreshPromise = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          return response.json();
        })
        .finally(() => {
          window.setTimeout(() => {
            FreeShippingProgress.refreshPromise = null;
          }, 0);
        });
    }
    return FreeShippingProgress.refreshPromise;
  }

  #render(total, itemCount, { variantOnly = false } = {}) {
    const threshold = Number(this.dataset.threshold || 79900);
    const remaining = Math.max(threshold - total, 0);
    const progress = Math.min((total / threshold) * 100, 100);
    const productMode = this.#isProductMode;
    const piecePrice = this.#piecePrice;
    const previewTotal = total + piecePrice;
    const pieceUnlocks = productMode && piecePrice > 0 && remaining > 0 && previewTotal >= threshold;
    const message = this.querySelector('[data-free-shipping-message]');
    const track = this.querySelector('[data-free-shipping-track]');
    const bar = this.querySelector('[data-free-shipping-bar]');
    const preview = this.querySelector('[data-free-shipping-preview]');
    const previousTotal = this.#lastTotal ?? total;
    this.#lastTotal = total;
    this.#itemCount = itemCount;
    this.#successPending = remaining === 0 && (this.#successPending || previousTotal < threshold);
    this.#cancelMotion();

    this.classList.toggle('free-shipping-progress--complete', remaining === 0);
    this.classList.toggle('free-shipping-progress--compact', productMode && itemCount === 0);
    this.classList.toggle('free-shipping-progress--unlocks', pieceUnlocks);

    if (message) {
      message.replaceChildren();
      if (pieceUnlocks || (productMode && itemCount === 0 && piecePrice >= threshold)) {
        message.textContent = 'Con esta pieza tu envío es gratis.';
      } else if (productMode && itemCount === 0 && piecePrice > 0) {
        message.append('Con esta pieza te faltarían ', this.#amountElement(threshold - piecePrice), ' para el envío gratis.');
      } else if (itemCount === 0) {
        message.textContent = 'Agrega productos a tu carrito para alcanzar el envío gratis.';
        this.#lastAmount = null;
      } else if (remaining > 0) {
        message.append('Te faltan ', this.#amountElement(remaining), ' para el envío gratis.');
        if (productMode && piecePrice > 0) {
          const hint = document.createElement('span');
          hint.className = 'free-shipping-progress__hint';
          hint.textContent = ` Con esta pieza, ${this.#formatMoney(threshold - previewTotal)}.`;
          message.append(hint);
        }
      } else {
        message.textContent = '¡Tu carrito alcanzó el envío gratis!';
        this.#lastAmount = null;
      }
    }

    if (preview instanceof HTMLElement) {
      preview.style.setProperty('--free-shipping-preview', String(Math.min(previewTotal / threshold, 1)));
    }

    if (bar instanceof HTMLElement) {
      bar.style.setProperty('--free-shipping-progress', String(progress / 100));
      // When this update reaches the goal, the celebration runs its own shine once the bar settles.
      if (!variantOnly && total > previousTotal && previousTotal < threshold && remaining > 0) {
        this.#animate(this.querySelector('[data-free-shipping-shine]'), [
          { transform: 'translateX(-100%)', opacity: 0 },
          { opacity: 1, offset: 0.2 },
          { transform: 'translateX(100%)', opacity: 0 },
        ], 760);
      }
      if (!variantOnly) this.#announceAnimationComplete(bar, { total, threshold, progress });
    }
    if (track instanceof HTMLElement) {
      track.setAttribute('aria-valuenow', String(Math.min(total, threshold)));
      track.setAttribute('aria-valuetext', `${this.#formatMoney(total)} de ${this.#formatMoney(threshold)}`);
    }
  }

  /**
   * Builds the highlighted amount. Screen readers get the final value; the visible number counts
   * from the previous amount to the new one.
   * @param {number} cents
   */
  #amountElement(cents) {
    const amount = document.createElement('strong');
    amount.className = 'free-shipping-progress__amount';
    const visible = document.createElement('span');
    visible.setAttribute('aria-hidden', 'true');
    const spoken = document.createElement('span');
    spoken.className = 'visually-hidden';
    spoken.textContent = this.#formatMoney(cents);
    amount.append(visible, spoken);

    const from = this.#lastAmount;
    this.#lastAmount = cents;
    this.#countTo(visible, from, cents);
    return amount;
  }

  /** @param {HTMLElement} element @param {number | null} from @param {number} to */
  #countTo(element, from, to) {
    window.cancelAnimationFrame(this.#countFrame);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (from == null || from === to || reduced) {
      element.textContent = this.#formatMoney(to);
      return;
    }

    const duration = 420;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      element.textContent = this.#formatMoney(Math.round(from + (to - from) * eased));
      if (t < 1) this.#countFrame = window.requestAnimationFrame(step);
    };
    element.textContent = this.#formatMoney(from);
    this.#countFrame = window.requestAnimationFrame(step);
  }

  #announceAnimationComplete(bar, detail) {
    this.#animationCleanup?.();

    let finished = false;
    let fallback;
    const complete = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(fallback);
      bar.removeEventListener('transitionend', onTransitionEnd);
      this.#animationCleanup = null;
      const dispatch = () => {
        this.dispatchEvent(
          new CustomEvent('free-shipping-progress:complete', {
            bubbles: true,
            detail,
          })
        );
      };
      if (this.#successPending) {
        this.#successPending = false;
        this.#celebrateSuccess();
        // Listeners (the "added to cart" popup) wait for the main part of the celebration.
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.#celebrationTimer = window.setTimeout(dispatch, CELEBRATION_LEAD_MS);
          return;
        }
      }
      dispatch();
    };
    const onTransitionEnd = (event) => {
      if (event.target === bar && event.propertyName === 'transform') complete();
    };

    bar.addEventListener('transitionend', onTransitionEnd);
    fallback = window.setTimeout(complete, 680);
    this.#animationCleanup = () => {
      finished = true;
      window.clearTimeout(fallback);
      bar.removeEventListener('transitionend', onTransitionEnd);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      queueMicrotask(complete);
    }
  }

  #animate(element, keyframes, duration, delay = 0) {
    if (!element?.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#motionAnimations.push(element.animate(keyframes, {
      duration,
      delay,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'backwards',
    }));
  }

  /** One-time celebration when the cart reaches free shipping. Everything settles; nothing loops. */
  #celebrateSuccess() {
    // Icon: pop with a soft rebound while the check draws itself.
    this.#animate(this.querySelector('[data-free-shipping-icon]'), [
      { transform: 'scale(1)' },
      { transform: 'scale(0.9)', offset: 0.15 },
      { transform: 'scale(1.2)', offset: 0.45 },
      { transform: 'scale(0.97)', offset: 0.7 },
      { transform: 'scale(1)' },
    ], 560);
    const checkPath = this.querySelector('.free-shipping-progress__check path');
    if (checkPath instanceof SVGPathElement) {
      checkPath.setAttribute('pathLength', '1');
      checkPath.style.strokeDasharray = '1';
      this.#animate(checkPath, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], 420, 120);
    }

    // Two staggered rings and six sparks, the same language as the favorites heart.
    this.#animate(this.querySelector('[data-free-shipping-ring]'), [
      { transform: 'scale(0.85)', opacity: 0.7 },
      { transform: 'scale(1.9)', opacity: 0 },
    ], 620);
    this.#animate(this.querySelector('[data-free-shipping-ring-late]'), [
      { transform: 'scale(0.85)', opacity: 0.45 },
      { transform: 'scale(2.4)', opacity: 0 },
    ], 760, 140);
    this.querySelectorAll('[data-free-shipping-spark]').forEach((spark) => {
      const angle = spark.style.getPropertyValue('--spark-angle') || '0deg';
      this.#animate(spark, [
        { transform: `rotate(${angle}) translateY(-15px) scale(1)`, opacity: 0 },
        { opacity: 1, offset: 0.3 },
        { transform: `rotate(${angle}) translateY(-25px) scale(0.4)`, opacity: 0 },
      ], 520, 80);
    });

    // Bar: an amethyst glow pulse and a single shine across the full track.
    this.#animate(this.querySelector('[data-free-shipping-bar]'), [
      { boxShadow: '0 0 14px rgb(157 78 221 / 0.42)' },
      { boxShadow: '0 0 26px 3px rgb(157 78 221 / 0.62)', offset: 0.35 },
      { boxShadow: '0 0 14px rgb(157 78 221 / 0.42)' },
    ], 1000);
    this.#animate(this.querySelector('[data-free-shipping-shine]'), [
      { transform: 'translateX(-100%)', opacity: 0 },
      { opacity: 1, offset: 0.2 },
      { transform: 'translateX(100%)', opacity: 0 },
    ], 900, 100);

    this.#animate(this.querySelector('[data-free-shipping-message]'), [
      { transform: 'translateY(4px)', opacity: 0.4 },
      { transform: 'translateY(0)', opacity: 1 },
    ], 320);
  }

  #cancelMotion() {
    this.#motionAnimations.forEach((animation) => animation.cancel());
    this.#motionAnimations = [];
  }

  #onMotionPreferenceChange = (event) => {
    if (event.matches) this.#cancelMotion();
  };

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'MXN';
    const format = this.dataset.moneyFormat;
    if (format) {
      try {
        return formatThemeMoney(cents, format, currency);
      } catch {
        /* fall back to Intl below */
      }
    }
    const amount = new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
    return `${amount} ${currency}`;
  }

  #formatRemaining(cents) {
    return this.#formatMoney(cents);
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
